import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { StripeProvider, Elements, CardNumberElement, CardExpiryElement, CardCVCElement, injectStripe } from 'react-stripe-elements';
import Helmet from 'react-helmet';

import AuthService from './AuthService';
import withAuth from './WithAuth';
import CartContext from '../contexts/CartContext';
import constants from '../config';

const Auth = new AuthService();

var TRACKING_UPGRADE_FEE = constants.TRACKING_UPGRADE_FEE || 5.50;
var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;
var TRACKING_REQUIRED_THRESHOLD = constants.TRACKING_REQUIRED_THRESHOLD || 50;
var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;

var US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS','MP',
];

var BRAND_ICONS = {
  visa: 'fa-cc-visa',
  mastercard: 'fa-cc-mastercard',
  amex: 'fa-cc-amex',
  discover: 'fa-cc-discover',
  diners: 'fa-cc-diners-club',
  jcb: 'fa-cc-jcb',
};

class CartCheckoutForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentMethod: 'card', // 'card' or 'paypal'
      submitting: false,
      error: '',
      shippingTo: '',
      street1: '',
      street2: '',
      city: '',
      addrState: '',
      zip: '',
      country: 'US',
      savedAddresses: [],
      selectedAddressId: null,
      // Saved cards
      savedCards: [],
      selectedCardId: null, // null = new card, string = saved card id
      saveNewCard: false,
      loadingCards: true,
      // Tax
      taxRate: 0,
      taxAmount: 0,
      taxLoading: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this._expiryEl = null;
    this._cvcEl = null;
    this._taxDebounce = null;
  }

  handleSubmit(e) {
    e.preventDefault();
    var self = this;
    var state = this.state;
    var cart = this.props.cart;
    var clearCart = this.props.clearCart;

    if (!state.shippingTo || !state.street1 || !state.city || !state.addrState || !state.zip || !state.country) {
      this.setState({ error: 'Please fill in all required address fields.' });
      return;
    }

    if (state.submitting) return;
    this.setState({ submitting: true, error: '' });

    // Build common body data
    var postIds = cart.map(function(item) { return item.postId; });
    var quantities = {};
    var trackingUpgrades = {};
    var sellerTotals = {};
    cart.forEach(function(item) {
      var sid = item.sellerId || 'unknown';
      if (!sellerTotals[sid]) sellerTotals[sid] = 0;
      sellerTotals[sid] += (Number(item.price) || 0) * (item.quantity || 1);
    });
    var sellerShip = {};
    Object.keys(sellerTotals).forEach(function(sid) {
      sellerShip[sid] = sellerTotals[sid] >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
    });
    var trackingSent = {};
    cart.forEach(function(item) {
      var sid = item.sellerId || 'unknown';
      quantities[item.postId] = item.quantity || 1;
      if (item.trackingUpgrade && sellerTotals[sid] < TRACKING_REQUIRED_THRESHOLD && !trackingSent[sid]) {
        trackingSent[sid] = true;
        trackingUpgrades[item.postId] = true;
      }
    });

    // Compute totals for confirmation page
    var cSubtotal = 0;
    var cShippingTotal = 0;
    var cTrackingTotal = 0;
    var cShipCounted = {};
    var cTrackCounted = {};
    cart.forEach(function(item) {
      var qty = item.quantity || 1;
      var sid = item.sellerId || 'unknown';
      cSubtotal += (Number(item.price) || 0) * qty;
      if (!cShipCounted[sid]) {
        cShipCounted[sid] = true;
        cShippingTotal += sellerShip[sid] || 0;
      }
      if (item.trackingUpgrade && sellerTotals[sid] < TRACKING_REQUIRED_THRESHOLD && !cTrackCounted[sid]) {
        cTrackCounted[sid] = true;
        cTrackingTotal += TRACKING_UPGRADE_FEE;
      }
    });
    var cTxFee = constants.TRANSACTION_FEE || 0.50;
    var cNumSellers = Object.keys(sellerTotals).length;
    var cTaxAmount = Math.round(cSubtotal * this.state.taxRate * 100) / 100;
    var cGrandTotal = cSubtotal + cShippingTotal + cTrackingTotal + (cTxFee * cNumSellers) + cTaxAmount;

    var shippingAddress = {
      street1: state.street1,
      street2: state.street2,
      city: state.city,
      state: state.addrState,
      zip: state.zip,
      country: state.country,
    };

    // Use saved card or new card
    if (state.selectedCardId) {
      // Saved card flow
      var customerBody = {
        postIds: postIds,
        quantities: quantities,
        trackingUpgrades: trackingUpgrades,
        shippingTo: state.shippingTo,
        shippingAddress: shippingAddress,
        billingAddress: shippingAddress,
      };

      Auth.fetch(constants.DOMAIN + '/api/v1/transactions/cart/customerbuy', {
        mode: 'cors',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerBody),
      })
        .then(function(res) {
          if (res.success) {
            clearCart();
            var selectedCard = state.savedCards.find(function(c) { return c.id === state.selectedCardId; });
            self.props.history.push('/order-confirmation', {
              cartPurchase: true,
              chargeId: res.chargeId || null,
              transactionId: res.transactionIds && res.transactionIds[0] ? res.transactionIds[0] : null,
              itemCount: cart.length,
              items: cart.map(function(item) {
                return {
                  title: item.title,
                  image: item.image || item.cardImage,
                  price: Number(item.price) || 0,
                  quantity: item.quantity || 1,
                  postId: item.postId,
                };
              }),
              subtotal: cSubtotal,
              shippingTotal: cShippingTotal,
              trackingUpgradeFee: cTrackingTotal,
              transactionFee: cTxFee * cNumSellers,
              taxAmount: cTaxAmount,
              grandTotal: cGrandTotal,
              shippingAddress: shippingAddress,
              shippingTo: state.shippingTo,
              paymentInfo: { method: 'card', brand: selectedCard ? selectedCard.brand : (res.cardBrand || null), last4: selectedCard ? selectedCard.last4 : (res.cardLast4 || null) },
            });
          } else {
            var errMsg = res.error || 'Payment failed';
            if (res.soldPostId) {
              self.props.removeFromCart(res.soldPostId);
              errMsg = 'An item was sold. It has been removed from your cart.';
            }
            self.setState({ submitting: false, error: errMsg });
          }
        })
        .catch(function(err) {
          console.log('Cart checkout error:', err);
          self.setState({ submitting: false, error: 'Payment failed. Please try again.' });
        });
    } else {
      // New card / Stripe token flow
      if (!this.props.stripe) {
        this.setState({ submitting: false, error: 'Payment system not loaded. Please try again.' });
        return;
      }

      this.props.stripe.createToken().then(function(payload) {
        if (payload.error) {
          self.setState({ submitting: false, error: payload.error.message });
          return;
        }

        var body = {
          token: payload.token.id,
          postIds: postIds,
          quantities: quantities,
          trackingUpgrades: trackingUpgrades,
          shippingTo: state.shippingTo,
          shippingAddress: shippingAddress,
          billingAddress: shippingAddress,
        };

        Auth.fetch(constants.DOMAIN + '/api/v1/transactions/cart/buy', {
          mode: 'cors',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
          .then(function(res) {
            if (res.success) {
              // Save card if checkbox was checked
              if (self.state.saveNewCard && res.chargeId) {
                Auth.fetch(constants.DOMAIN + '/api/v1/transactions/payment-methods/save-from-charge', {
                  mode: 'cors',
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ chargeId: res.chargeId }),
                }).catch(function() {});
              }
              clearCart();
              self.props.history.push('/order-confirmation', {
                cartPurchase: true,
                chargeId: res.chargeId || null,
                transactionId: res.transactionIds && res.transactionIds[0] ? res.transactionIds[0] : null,
                itemCount: cart.length,
                items: cart.map(function(item) {
                  return {
                    title: item.title,
                    image: item.image,
                    price: Number(item.price) || 0,
                    quantity: item.quantity || 1,
                    postId: item.postId,
                  };
                }),
                subtotal: cSubtotal,
                shippingTotal: cShippingTotal,
                trackingUpgradeFee: cTrackingTotal,
                transactionFee: cTxFee * cNumSellers,
                taxAmount: cTaxAmount,
                grandTotal: cGrandTotal,
                shippingAddress: shippingAddress,
                shippingTo: state.shippingTo,
                paymentInfo: { method: 'card', brand: res.cardBrand || null, last4: res.cardLast4 || null },
              });
            } else {
              var errMsg = res.error || 'Payment failed';
              if (res.soldPostId) {
                self.props.removeFromCart(res.soldPostId);
                errMsg = 'An item was sold. It has been removed from your cart.';
              }
              self.setState({ submitting: false, error: errMsg });
            }
          })
          .catch(function(err) {
            console.log('Cart checkout error:', err);
            self.setState({ submitting: false, error: 'Payment failed. Please try again.' });
          });
      });
    }
  }

  componentDidMount() {
    this.loadPayPalSDK();
    this.fetchSavedAddresses();
    this.fetchSavedCards();
    this._handleThemeChanged = function() { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);
  }

  componentWillUnmount() {
    window.removeEventListener('themeChanged', this._handleThemeChanged);
  }

  get shippingValid() {
    var s = this.state;
    return !!(s.shippingTo && s.street1 && s.city && s.addrState && s.zip && s.country);
  }

  componentDidUpdate(prevProps, prevState) {
    var wasValid = !!(prevState.shippingTo && prevState.street1 && prevState.city && prevState.addrState && prevState.zip && prevState.country);
    var isValid = this.shippingValid;
    if (!wasValid && isValid && this.state.paymentMethod === 'paypal') {
      this.renderPayPalButton();
    }
  }

  fetchSavedCards() {
    var self = this;
    if (!Auth.getToken()) return;
    Auth.fetch(constants.DOMAIN + '/api/v1/transactions/payment-methods', {
      mode: 'cors',
      method: 'GET',
    }).then(function(res) {
      var cards = (res && res.cards) || [];
      self.setState({ savedCards: cards, loadingCards: false });
      if (cards.length > 0) {
        var defaultCard = cards.find(function(c) { return c.isDefault; });
        self.setState({ selectedCardId: defaultCard ? defaultCard.id : cards[0].id });
      }
    }).catch(function() { self.setState({ loadingCards: false }); });
  }

  fetchSavedAddresses() {
    var self = this;
    if (!Auth.getToken()) return;
    try {
      var profile = Auth.getProfile();
      var addressesPromise = Auth.fetch(constants.DOMAIN + '/api/v1/users/addresses', {
        mode: 'cors',
        method: 'GET',
      }).catch(function() { return { addresses: [] }; });
      var profilePromise = Auth.fetch(constants.DOMAIN + '/api/v1/users/' + profile._id, {
        mode: 'cors',
        method: 'GET',
      }).catch(function() { return { profile: {} }; });
      Promise.all([addressesPromise, profilePromise]).then(function(results) {
        var addresses = (results[0] && results[0].addresses) || [];
        var fullName = (results[1] && results[1].profile && results[1].profile.name) || '';
        self.setState({ savedAddresses: addresses });
        var defaultAddr = addresses.find(function(a) { return a.isDefault; });
        if (defaultAddr) {
          self.applyAddress(defaultAddr, fullName);
        } else if (addresses.length > 0) {
          self.applyAddress(addresses[0], fullName);
        } else if (fullName) {
          self.setState({ shippingTo: fullName });
        }
      });
    } catch (e) {}
  }

  applyAddress(addr, name) {
    var update = { selectedAddressId: addr._id };
    if (name && !this.state.shippingTo) update.shippingTo = name;
    if (addr.street1) update.street1 = addr.street1;
    update.street2 = addr.street2 || '';
    if (addr.city) update.city = addr.city;
    if (addr.state) update.addrState = addr.state;
    if (addr.zip) update.zip = addr.zip;
    if (addr.country) update.country = addr.country;
    this.setState(update);
    // Fetch tax rate immediately when applying saved address
    if (addr.zip && addr.state) {
      this.fetchTaxRate(addr.zip, addr.state);
    }
  }

  fetchTaxRate(zip, state) {
    var self = this;
    var cleanZip = (zip || '').trim();
    var cleanState = (state || '').trim();
    if (cleanZip.length !== 5 || !cleanState) {
      self.setState({ taxRate: 0, taxAmount: 0, taxLoading: false });
      return;
    }
    self.setState({ taxLoading: true });
    fetch(constants.DOMAIN + '/api/v1/guest/tax-rate?zip=' + encodeURIComponent(cleanZip) + '&state=' + encodeURIComponent(cleanState))
      .then(function(resp) { return resp.json(); })
      .then(function(data) {
        self.setState({ taxRate: data.rate || 0, taxLoading: false });
      })
      .catch(function() {
        self.setState({ taxRate: 0, taxLoading: false });
      });
  }

  debouncedFetchTaxRate(zip, state) {
    var self = this;
    if (self._taxDebounce) clearTimeout(self._taxDebounce);
    self._taxDebounce = setTimeout(function() {
      self.fetchTaxRate(zip, state);
    }, 300);
  }

  loadPayPalSDK() {
    var self = this;
    // Use modern PayPal JS SDK
    if (window.paypal && window.paypal.Buttons) {
      self.renderPayPalButton();
      return;
    }
    // Remove old checkout.js if loaded
    var oldScript = document.querySelector('script[src*="paypalobjects.com/api/checkout.js"]');
    if (oldScript) oldScript.parentNode.removeChild(oldScript);
    var script = document.createElement('script');
    script.src = 'https://www.paypal.com/sdk/js?client-id=' + constants.PAYPAL_CLIENT_ID + '&currency=USD';
    script.async = true;
    script.onload = function() {
      self.renderPayPalButton();
    };
    document.body.appendChild(script);
  }

  renderPayPalButton() {
    var self = this;
    var container = document.getElementById('cart-paypal-button');
    if (!window.paypal || !window.paypal.Buttons || !container) return;
    container.innerHTML = '';

    window.paypal.Buttons({
      style: {
        color: 'gold',
        shape: 'rect',
        layout: 'vertical',
      },
      createOrder: function() {
        self.setState({ error: '' });
        var cart = self.props.cart;
        var postIds = cart.map(function(item) { return item.postId; });

        var quantities = {};
        var trackingUpgrades = {};
        var ppSellerTotals = {};
        cart.forEach(function(item) {
          var sid = item.sellerId || 'unknown';
          if (!ppSellerTotals[sid]) ppSellerTotals[sid] = 0;
          ppSellerTotals[sid] += (Number(item.price) || 0) * (item.quantity || 1);
        });
        var trackingSent = {};
        cart.forEach(function(item) {
          var sid = item.sellerId || 'unknown';
          quantities[item.postId] = item.quantity || 1;
          if (item.trackingUpgrade && ppSellerTotals[sid] < TRACKING_REQUIRED_THRESHOLD && !trackingSent[sid]) {
            trackingSent[sid] = true;
            trackingUpgrades[item.postId] = true;
          }
        });

        localStorage.setItem('primal_cart_pending_paypal', JSON.stringify(postIds));

        return fetch(constants.DOMAIN + '/api/v1/transactions/cart/paypalbuy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': Auth.getAuthHeader(),
          },
          body: JSON.stringify({ postIds: postIds, quantities: quantities, trackingUpgrades: trackingUpgrades, shippingAddress: { street1: self.state.street1, street2: self.state.street2, city: self.state.city, state: self.state.addrState, zip: self.state.zip, country: self.state.country } }),
        })
        .then(function(resp) { return resp.json(); })
        .then(function(res) {
          if (res.url) {
            // Extract order ID from PayPal approval URL
            var match = res.url.match(/token=([^&]+)/);
            if (match) return match[1];
          }
          throw new Error('Failed to create PayPal order');
        });
      },
      onApprove: function(data) {
        // Save shipping/payment info for confirmation page (lost on redirect)
        try {
          localStorage.setItem('primal_order_meta', JSON.stringify({
            shippingAddress: { street1: self.state.street1, street2: self.state.street2, city: self.state.city, state: self.state.addrState, zip: self.state.zip, country: self.state.country },
            shippingTo: self.state.shippingTo,
            paymentInfo: { method: 'paypal' },
          }));
        } catch (e) {}
        // Redirect to order confirmation for capture
        window.location.href = '/order-confirmation?paypal_type=cart&token=' + data.orderID;
      },
      onCancel: function() {
        self.setState({ error: 'Payment cancelled. You can try again when ready.' });
      },
      onError: function(err) {
        console.error('PayPal error:', err);
        self.setState({ error: 'Something went wrong with PayPal. Please try again.' });
      },
    }).render('#cart-paypal-button');
  }

  render() {
    var self = this;
    var state = this.state;
    var cart = this.props.cart;
    var api = constants.DOMAIN + '/';
    var usingSavedCard = !!state.selectedCardId;

    // Calculate seller subtotals first
    var renderSellerSubtotals = {};
    cart.forEach(function(item) {
      var sid = item.sellerId || 'unknown';
      if (!renderSellerSubtotals[sid]) renderSellerSubtotals[sid] = 0;
      renderSellerSubtotals[sid] += (Number(item.price) || 0) * (item.quantity || 1);
    });
    // Platform shipping per seller
    var renderSellerShipping = {};
    Object.keys(renderSellerSubtotals).forEach(function(sid) {
      renderSellerShipping[sid] = renderSellerSubtotals[sid] >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
    });
    var subtotal = 0;
    var shippingTotal = 0;
    var trackingUpgradeTotal = 0;
    var totalItems = 0;
    var renderTrackingSellers = {};
    var renderShippingCounted = {};
    cart.forEach(function(item) {
      var qty = item.quantity || 1;
      var sid = item.sellerId || 'unknown';
      subtotal += (Number(item.price) || 0) * qty;
      if (!renderShippingCounted[sid]) {
        renderShippingCounted[sid] = true;
        shippingTotal += renderSellerShipping[sid] || 0;
      }
      if (item.trackingUpgrade && renderSellerSubtotals[sid] < TRACKING_REQUIRED_THRESHOLD && !renderTrackingSellers[sid]) {
        renderTrackingSellers[sid] = true;
        trackingUpgradeTotal += TRACKING_UPGRADE_FEE;
      }
      totalItems += qty;
    });
    var TRANSACTION_FEE = constants.TRANSACTION_FEE || 0.50;
    var numSellers = Object.keys(renderSellerSubtotals).length;
    var totalTransactionFee = TRANSACTION_FEE * numSellers;
    var taxAmount = Math.round(subtotal * state.taxRate * 100) / 100;
    var grandTotal = subtotal + shippingTotal + trackingUpgradeTotal + totalTransactionFee + taxAmount;

    var inputClass = 'tw-w-full tw-px-4 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold';
    var checkSvg = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 20 20' fill='%2322c55e' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'/%3E%3C/svg%3E\")";
    var validBorder = '#22c55e';
    var isNameValid = state.shippingTo.trim().length > 0;
    var isStreet1Valid = state.street1.trim().length > 0;
    var isCityValid = state.city.trim().length > 0;
    var isStateValid = state.addrState !== '';
    var isZipValid = /^\d{5}$/.test(state.zip.trim());
    function validStyle(isValid, extra) {
      if (!isValid) return extra || undefined;
      var s = { borderColor: validBorder, paddingRight: '36px', backgroundImage: checkSvg, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: '16px' };
      return extra ? Object.assign({}, extra, s) : s;
    }

    return (
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-10">
        <Link to="/cart" className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 hover:tw-text-brand-gold tw-no-underline tw-transition-colors tw-mb-5 tw-inline-flex tw-items-center tw-gap-1.5">
          <i className="fa fa-arrow-left" style={{ fontSize: '9px' }} /> Back to Cart
        </Link>
        <h1 className="tw-text-2xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-8">Checkout</h1>

        <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-8">
          {/* Order Summary — first on mobile, right on desktop */}
          <div className="md:tw-order-last tw-w-full md:tw-w-96 tw-flex-shrink-0">
            <div className="tw-bg-white dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-p-6">
              <h2 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-5">
                Order Summary ({totalItems} item{totalItems !== 1 ? 's' : ''})
              </h2>
              <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-4">
                {(function() {
                  var shippingShown = {};
                  return cart.map(function(item) {
                    var imgSrc = item.image ? (item.image.startsWith('http') ? item.image : api + (item.image.startsWith('/') ? '' : '/') + item.image) : null;
                    var sid = item.sellerId || 'unknown';
                    var isFirstForSeller = !shippingShown[sid];
                    if (isFirstForSeller) shippingShown[sid] = true;
                    var sellerShip = renderSellerShipping[sid] || 0;
                    var hasTracking = !!renderTrackingSellers[sid];
                    return (
                      <div key={item.postId} className="tw-flex tw-items-center tw-gap-3">
                        {imgSrc ? (
                          <img src={imgSrc} alt={item.title} className="tw-w-14 tw-h-14 tw-rounded-lg tw-object-contain tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#1a1a1a] tw-flex-shrink-0" />
                        ) : (
                          <div className="tw-w-14 tw-h-14 tw-rounded-lg tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                            <i className="fa fa-image tw-text-[#ccc] dark:tw-text-[#555] tw-text-sm" />
                          </div>
                        )}
                        <div className="tw-flex-1 tw-min-w-0">
                          <p className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-mb-0">
                            {item.title}
                            {(item.quantity || 1) > 1 ? ' x' + (item.quantity || 1) : ''}
                          </p>
                          <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mb-0">
                            {'$' + ((Number(item.price) || 0) * (item.quantity || 1)).toFixed(2)}
                            {isFirstForSeller && sellerShip > 0 ? (' + $' + sellerShip.toFixed(2) + ' shipping') : ''}
                            {isFirstForSeller && sellerShip === 0 ? ' · Free Shipping' : ''}
                            {!isFirstForSeller ? ' · Shipping Included' : ''}
                            {isFirstForSeller && hasTracking ? ' · Tracking' : ''}
                          </p>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="tw-border-t tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-pt-4">
                <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-1.5">
                  <span>Subtotal</span>
                  <span>{'$' + subtotal.toFixed(2)}</span>
                </div>
                <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-1">
                  <span>Shipping</span>
                  {shippingTotal > 0 ? (
                    <span>{'$' + shippingTotal.toFixed(2)}</span>
                  ) : (
                    <span className="tw-text-green-600 dark:tw-text-green-400">Free</span>
                  )}
                </div>
                {trackingUpgradeTotal > 0 && (
                  <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-1">
                    <span>Tracking Upgrades</span>
                    <span>{'$' + trackingUpgradeTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-1">
                  <span>Transaction Fee{numSellers > 1 ? ' ($' + TRANSACTION_FEE.toFixed(2) + ' \u00d7 ' + numSellers + ' sellers)' : ''}</span>
                  <span>{'$' + totalTransactionFee.toFixed(2)}</span>
                </div>
                <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-1">
                  <span>
                    {'Estimated Tax'}
                    {state.taxRate > 0 ? ' (' + (state.taxRate * 100).toFixed(2) + '%)' : ''}
                  </span>
                  {state.taxLoading ? (
                    <span className="tw-text-[#999] dark:tw-text-gray-500"><i className="fa fa-spinner fa-spin" style={{ fontSize: '10px' }} /></span>
                  ) : state.zip.trim().length === 5 && state.addrState.trim() ? (
                    <span>{'$' + taxAmount.toFixed(2)}</span>
                  ) : (
                    <span className="tw-italic tw-text-[#999] dark:tw-text-gray-500" style={{ fontSize: '12px' }}>Enter zip for tax</span>
                  )}
                </div>
                <div className="tw-flex tw-justify-between tw-items-baseline tw-mt-3 tw-pt-3 tw-border-t tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a]">
                  <span className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">Total</span>
                  <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">{'$' + grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Form — second on mobile, left on desktop */}
          <div className="tw-flex-1">
            <form onSubmit={this.handleSubmit}>
              <style>{
                document.documentElement.classList.contains('tw-dark') ? `
                  input:-webkit-autofill,
                  input:-webkit-autofill:hover,
                  input:-webkit-autofill:focus,
                  select:-webkit-autofill,
                  select:-webkit-autofill:hover,
                  select:-webkit-autofill:focus {
                    -webkit-box-shadow: 0 0 0 1000px #141414 inset !important;
                    -webkit-text-fill-color: #ffffff !important;
                    border-color: #555 !important;
                    transition: background-color 5000s ease-in-out 0s;
                  }
                ` : ''
              }</style>
              {/* Shipping Address */}
              <div className="tw-mb-8">
                <h2 className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-uppercase tw-tracking-wider tw-mb-4">Shipping Address</h2>
                {/* Saved address selector */}
                {state.savedAddresses.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                    {state.savedAddresses.map(function(addr) {
                      var isSelected = state.selectedAddressId === addr._id;
                      return (
                        <button
                          key={addr._id}
                          type="button"
                          onClick={function() { self.applyAddress(addr, state.shippingTo); }}
                          className={'tw-text-left tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs tw-cursor-pointer tw-transition-colors tw-border ' + (
                            isSelected
                              ? 'tw-border-brand-gold tw-bg-brand-gold/10'
                              : 'tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#141414] hover:tw-border-brand-gold/50'
                          )}
                        >
                          <span className={'tw-font-semibold tw-block ' + (isSelected ? 'tw-text-brand-gold' : 'tw-text-[#1a1a1a] dark:tw-text-white')}>
                            {addr.label}{addr.isDefault ? ' (Default)' : ''}
                          </span>
                          <span className="tw-text-[#888] tw-block tw-mt-0.5">{addr.street1}, {addr.city}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="tw-flex tw-flex-col tw-gap-3">
                  <input
                    type="text"
                    placeholder="Full name"
                    value={state.shippingTo}
                    onChange={function(e) { this.setState({ shippingTo: e.target.value }); }.bind(this)}
                    className={inputClass}
                    style={validStyle(isNameValid)}
                  />
                  <input
                    type="text"
                    placeholder="Street address"
                    value={state.street1}
                    onChange={function(e) { this.setState({ street1: e.target.value }); }.bind(this)}
                    className={inputClass}
                    style={validStyle(isStreet1Valid)}
                  />
                  <input
                    type="text"
                    placeholder="Street address 2 (optional)"
                    value={state.street2}
                    onChange={function(e) { this.setState({ street2: e.target.value }); }.bind(this)}
                    className={inputClass}
                  />
                  <div className="tw-flex tw-gap-3">
                    <input
                      type="text"
                      placeholder="City"
                      value={state.city}
                      onChange={function(e) { this.setState({ city: e.target.value }); }.bind(this)}
                      className={inputClass + ' tw-flex-1'}
                      style={validStyle(isCityValid)}
                    />
                    <select
                      value={state.addrState}
                      onChange={function(e) { var v = e.target.value; this.setState({ addrState: v }); this.debouncedFetchTaxRate(this.state.zip, v); }.bind(this)}
                      className={inputClass}
                      style={{ width: '140px', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', paddingRight: '32px', backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 20 20' fill='%23999' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: '16px', cursor: 'pointer', borderColor: isStateValid ? validBorder : undefined }}
                    >
                      <option value="">State</option>
                      {US_STATES.map(function(st) { return <option key={st} value={st}>{st}</option>; })}
                    </select>
                  </div>
                  <div className="tw-flex tw-gap-3">
                    <input
                      type="text"
                      placeholder="ZIP code"
                      value={state.zip}
                      onChange={function(e) { var v = e.target.value; this.setState({ zip: v }); this.debouncedFetchTaxRate(v, this.state.addrState); }.bind(this)}
                      className={inputClass}
                      style={validStyle(isZipValid, { width: '120px' })}
                    />
                    {/* USA-only: country locked to US. Uncomment below to allow international */}
                    {/* <input
                      type="text"
                      placeholder="Country"
                      value={state.country}
                      onChange={function(e) { this.setState({ country: e.target.value }); }.bind(this)}
                      className={inputClass + ' tw-flex-1'}
                    /> */}
                    <input type="text" value="US" readOnly className={inputClass + ' tw-flex-1 tw-opacity-60 tw-cursor-not-allowed'} />
                  </div>
                </div>
              </div>

              {/* Payment Method Toggle */}
              <div className="tw-mb-6">
                <h2 className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-uppercase tw-tracking-wider tw-mb-3">Payment Method</h2>
                <div className="tw-flex tw-gap-3">
                  <button
                    type="button"
                    onClick={function() { this.setState({ paymentMethod: 'card' }); }.bind(this)}
                    className={'tw-flex-1 tw-py-2.5 tw-px-4 tw-rounded-lg tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-colors tw-border ' + (
                      state.paymentMethod === 'card'
                        ? 'tw-border-brand-gold tw-bg-brand-gold/10 tw-text-brand-gold'
                        : 'tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-transparent tw-text-[#666] dark:tw-text-gray-400 hover:tw-border-brand-gold'
                    )}
                  >
                    <i className="fa fa-credit-card tw-mr-2" />
                    Pay with Card
                  </button>
                  <button
                    type="button"
                    onClick={function() { this.setState({ paymentMethod: 'paypal' }, function() { if (this.shippingValid) this.renderPayPalButton(); }.bind(this)); }.bind(this)}
                    className={'tw-flex-1 tw-py-2.5 tw-px-4 tw-rounded-lg tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-colors tw-border ' + (
                      state.paymentMethod === 'paypal'
                        ? 'tw-border-brand-gold tw-bg-brand-gold/10 tw-text-brand-gold'
                        : 'tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-transparent tw-text-[#666] dark:tw-text-gray-400 hover:tw-border-brand-gold'
                    )}
                  >
                    <i className="fa fa-paypal tw-mr-2" />
                    Pay with PayPal
                  </button>
                </div>
              </div>

              {/* Saved Cards + New Card Selection */}
              {state.paymentMethod === 'card' && (
                <div className="tw-mb-6">
                  {/* Loading saved cards */}
                  {state.loadingCards && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-py-3 tw-mb-3">
                      <i className="fa fa-spinner fa-spin tw-text-brand-gold" />
                      <span className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400">Loading saved cards...</span>
                    </div>
                  )}
                  {/* Saved card radio options */}
                  {!state.loadingCards && state.savedCards.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-3">
                      {state.savedCards.map(function(card) {
                        var isSelected = state.selectedCardId === card.id;
                        var brandIcon = BRAND_ICONS[card.brand] || 'fa-credit-card';
                        return (
                          <label
                            key={card.id}
                            className={'tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-lg tw-cursor-pointer tw-transition-colors tw-border ' + (
                              isSelected
                                ? 'tw-border-brand-gold tw-bg-brand-gold/5'
                                : 'tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#141414] hover:tw-border-brand-gold/50'
                            )}
                          >
                            <input
                              type="radio"
                              name="cartCardSelection"
                              checked={isSelected}
                              onChange={function() { self.setState({ selectedCardId: card.id }); }}
                              className="tw-w-4 tw-h-4 tw-accent-brand-gold"
                            />
                            <i className={'fa ' + brandIcon + ' tw-text-lg ' + (isSelected ? 'tw-text-brand-gold' : 'tw-text-[#666] dark:tw-text-gray-400')} />
                            <span className={'tw-text-sm tw-font-medium ' + (isSelected ? 'tw-text-[#1a1a1a] dark:tw-text-white' : 'tw-text-[#666] dark:tw-text-gray-400')}>
                              {'····' + card.last4}
                            </span>
                            <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500">
                              {card.expMonth + '/' + card.expYear}
                            </span>
                            {card.isDefault && (
                              <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-brand-gold/15 tw-text-brand-gold">Default</span>
                            )}
                          </label>
                        );
                      })}
                      {/* New card option */}
                      <label
                        className={'tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-lg tw-cursor-pointer tw-transition-colors tw-border ' + (
                          !state.selectedCardId
                            ? 'tw-border-brand-gold tw-bg-brand-gold/5'
                            : 'tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#141414] hover:tw-border-brand-gold/50'
                        )}
                      >
                        <input
                          type="radio"
                          name="cartCardSelection"
                          checked={!state.selectedCardId}
                          onChange={function() { self.setState({ selectedCardId: null }); }}
                          className="tw-w-4 tw-h-4 tw-accent-brand-gold"
                        />
                        <i className={'fa fa-plus-circle tw-text-lg ' + (!state.selectedCardId ? 'tw-text-brand-gold' : 'tw-text-[#666] dark:tw-text-gray-400')} />
                        <span className={'tw-text-sm tw-font-medium ' + (!state.selectedCardId ? 'tw-text-[#1a1a1a] dark:tw-text-white' : 'tw-text-[#666] dark:tw-text-gray-400')}>
                          Use a new card
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Stripe Card Elements — show only when new card is selected and cards done loading */}
                  {!usingSavedCard && !state.loadingCards && (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                      <div className="tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#141414]">
                        <CardNumberElement
                          onChange={function(e) { if (e.complete && self._expiryEl) self._expiryEl.focus(); }}
                          style={{ base: { fontSize: '14px', color: document.documentElement.classList.contains('tw-dark') ? '#ffffff' : '#1a1a1a', '::placeholder': { color: document.documentElement.classList.contains('tw-dark') ? '#666' : '#aab7c4' } } }}
                        />
                      </div>
                      <div className="tw-flex tw-gap-3">
                        <div className="tw-flex-1 tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#141414]">
                          <CardExpiryElement
                            onReady={function(el) { self._expiryEl = el; }}
                            onChange={function(e) { if (e.complete && self._cvcEl) self._cvcEl.focus(); }}
                            style={{ base: { fontSize: '14px', color: document.documentElement.classList.contains('tw-dark') ? '#ffffff' : '#1a1a1a', '::placeholder': { color: document.documentElement.classList.contains('tw-dark') ? '#666' : '#aab7c4' } } }}
                          />
                        </div>
                        <div className="tw-flex-1 tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#141414]">
                          <CardCVCElement
                            onReady={function(el) { self._cvcEl = el; }}
                            style={{ base: { fontSize: '14px', color: document.documentElement.classList.contains('tw-dark') ? '#ffffff' : '#1a1a1a', '::placeholder': { color: document.documentElement.classList.contains('tw-dark') ? '#666' : '#aab7c4' } } }}
                          />
                        </div>
                      </div>
                      {/* Save card checkbox */}
                      <div className="tw-flex tw-items-center tw-gap-3 tw-mt-1">
                        <input
                          type="checkbox"
                          id="saveNewCardCart"
                          checked={state.saveNewCard}
                          onChange={function() { self.setState({ saveNewCard: !state.saveNewCard }); }}
                          className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-accent-brand-gold"
                        />
                        <label htmlFor="saveNewCardCart" className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-0 tw-leading-normal tw-cursor-pointer">
                          Save this card for future purchases
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {state.error && (
                <div className="tw-bg-red-600/10 tw-border tw-border-red-600/30 tw-rounded-lg tw-py-2.5 tw-px-4 tw-mb-4">
                  <p className="tw-text-red-500 tw-text-sm tw-mb-0">{state.error}</p>
                </div>
              )}

              {state.paymentMethod === 'card' && (
                <button
                  type="submit"
                  disabled={state.submitting}
                  className={'tw-w-full tw-py-3.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border-none tw-cursor-pointer ' + (
                    state.submitting
                      ? 'tw-bg-[#e0e0e0] dark:tw-bg-[#333] tw-text-[#999] tw-cursor-not-allowed'
                      : 'tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black'
                  )}
                  style={state.submitting ? {} : { boxShadow: '0 0 20px rgba(201, 168, 76, 0.2)', transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 250ms ease, background-color 250ms ease' }}
                  onMouseEnter={state.submitting ? undefined : function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(201, 168, 76, 0.4)'; }}
                  onMouseLeave={state.submitting ? undefined : function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(201, 168, 76, 0.2)'; }}
                >
                  {state.submitting ? 'Processing...' : 'Pay $' + grandTotal.toFixed(2)}
                </button>
              )}

              {/* PayPal SDK button — only rendered when shipping is valid */}
              {state.paymentMethod === 'paypal' && (
                this.shippingValid
                  ? <div id="cart-paypal-button"></div>
                  : <div className="tw-bg-gray-100 dark:tw-bg-[#111] tw-rounded-lg tw-p-4 tw-text-center tw-text-[#888] dark:tw-text-gray-500 tw-text-sm">
                      Complete your shipping info to continue to payment.
                    </div>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }
}

var InjectedCheckoutForm = injectStripe(CartCheckoutForm);

class CartCheckout extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  render() {
    var history = this.props.history;

    return (
      <div>
        <Helmet>
          <title>Checkout - Primal Marketplace</title>
        </Helmet>
        <CartContext.Consumer>
          {function(cartCtx) {
            if (!cartCtx.cart || cartCtx.cart.length === 0) {
              return (
                <div className="tw-max-w-3xl tw-mx-auto tw-px-4 tw-py-20 tw-text-center">
                  <div className="tw-w-16 tw-h-16 tw-mx-auto tw-mb-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a]">
                    <i className="fa fa-shopping-cart tw-text-2xl tw-text-[#ccc] dark:tw-text-[#444]" />
                  </div>
                  <p className="tw-text-lg tw-font-semibold tw-text-[#555] dark:tw-text-gray-400 tw-mb-1.5">Your cart is empty</p>
                  <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-8">Add some cards to get started</p>
                  <Link
                    to="/browse"
                    className="tw-inline-block tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-3 tw-px-8 tw-rounded-xl tw-text-sm tw-no-underline"
                    style={{ boxShadow: '0 0 20px rgba(201, 168, 76, 0.2)', transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 250ms ease, background-color 250ms ease' }}
                    onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(201, 168, 76, 0.4)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(201, 168, 76, 0.2)'; }}
                  >
                    Browse Cards
                  </Link>
                </div>
              );
            }
            return (
              <StripeProvider apiKey={constants.STRIPE_API_KEY}>
                <Elements>
                  <InjectedCheckoutForm
                    cart={cartCtx.cart}
                    clearCart={cartCtx.clearCart}
                    removeFromCart={cartCtx.removeFromCart}
                    history={history}
                  />
                </Elements>
              </StripeProvider>
            );
          }}
        </CartContext.Consumer>
      </div>
    );
  }
}

export default withAuth(CartCheckout);
